import { LiveAnnouncer } from "@angular/cdk/a11y";
import { CommonModule } from "@angular/common";
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Injector, OnInit, ViewRef, forwardRef, input, linkedSignal, model, signal, viewChild } from "@angular/core";
import { AbstractControl, ControlValueAccessor, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgControl, ValidationErrors, Validator } from "@angular/forms";
import { BiTranslateDirective, BiTranslatePipe, BiTranslateService } from "@globals/bi-translate";
import { CustomValidators } from "@globals/classes/CustomValidators";
import { BiCountryId } from "@globals/enums/BiLanguageAndCountryId";
import { isStringNullOrEmpty, uniqueID } from "@globals/helper-functions";
import { PhoneNumberTypeEnum } from "@globals/openapi-models/model/phoneNumberTypeEnum";
import { BiLocalizationHelperBaseService } from "@globals/services/bi-localization-helper-base.service";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { CountryCode, getCountries, getCountryCallingCode } from "libphonenumber-js";
import { OverlayOptions, SelectItem } from "primeng/api";
import { Dropdown, DropdownModule } from "primeng/dropdown";
import { InputTextModule } from "primeng/inputtext";
import { debounceTime, distinctUntilChanged, filter, take } from "rxjs";
import { callingCodeToCountryCode } from "./CallingCodeToCountryCode";
import { countryCodeToDeCountryName } from "./CountryCodeToDeCountryName";
import { countryCodeToDkCountryName } from "./CountryCodeToDkCountryName";
import { countryCodeToEnCountryName } from "./CountryCodeToEnCountryName";
import { countryCodeToFiCountryName } from "./CountryCodeToFiCountryName";
import { countryCodeToNoCountryName } from "./CountryCodeToNoCountryName";
import { countryCodeToSeCountryName } from "./CountryCodeToSeCountryName";

@UntilDestroy()
@Component({
  selector: "bi-phone-input",
  imports: [BiTranslatePipe, BiTranslateDirective, CommonModule, FormsModule, InputTextModule, DropdownModule],
  templateUrl: "./bi-phone-input.component.html",
  styleUrls: ["./bi-phone-input.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => BiPhoneInputComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: forwardRef(() => BiPhoneInputComponent)
    },
    BiLocalizationHelperBaseService
  ]
})
export class BiPhoneInputComponent implements ControlValueAccessor, Validator, AfterViewInit, OnInit {
  public callingCodeToCountryCode = callingCodeToCountryCode;
  public countryCodeToDkCountryName = countryCodeToDkCountryName;
  public countryCodeToSeCountryName = countryCodeToSeCountryName;
  public countryCodeToNoCountryName = countryCodeToNoCountryName;
  public countryCodeToFiCountryName = countryCodeToFiCountryName;
  public countryCodeToEnCountryName = countryCodeToEnCountryName;
  public countryCodeToDeCountryName = countryCodeToDeCountryName;

  /**
   * Used for setting both the phone prefix and also the country id (for validation purposes).
   */
  readonly countryId = model<BiCountryId>();

  /**
   * Allow the input to be disabled, Default false
   */
  readonly disabled = model(false);

  readonly focusable = input(true);

  /**
   * Set true if you want the input to get autofocus on load
   */
  readonly autofocus = input(false);

  /**
   * Whether the phone country code will be a selectable value
   */
  readonly canSelectCountryCode = input(false);
  public readonly selectedCountryCode = signal<number>(undefined);

  /**
   * Whether the phone type will be a selectable value
   */
  readonly canSelectPhoneType = input(false);
  public readonly selectedPhoneType = signal<PhoneNumberTypeEnum>(undefined);

  public phoneNumberTypes: Array<SelectItem> = [
    {
      label: this.translator.instant("shared.mobile"),
      value: PhoneNumberTypeEnum.Mobile
    },
    {
      label: this.translator.instant("shared.landline"),
      value: PhoneNumberTypeEnum.LandLine
    }
  ];

  readonly phoneCode = model<number>(undefined);
  readonly phoneType = model<PhoneNumberTypeEnum>(undefined);

  readonly label = model<string>(undefined);
  readonly hasLabel = input(true);

  /**
   * Whether the phone number to be entered is a landline (fastnet) number.
   * Used for validation
   */
  readonly isLandLine = model(false);

  readonly onlyMainCountries = input(false);

  private _phonePrefix: string;

  public readonly selectedCountryItem = linkedSignal(() => {
    return this.countryCodes.find(item => item.value === this.selectedCountryCode()) ?? undefined;
  });

  public get phonePrefix() {
    return this._phonePrefix;
  }

  public readonly inputValue = signal<string>(undefined);

  public readonly phoneLength = signal<number>(undefined);
  public readonly phoneMaxLength = signal<number>(undefined);

  /**
   * Tells whether the required length of phone number is
   * the MINIMUM required length. Default is false meaning that the phone length is the exact required length
   */
  public readonly phoneLengthIsMinimum = signal(false);

  /**
   * HTML input id
   */
  public readonly inputId = uniqueID();

  /**
   * HTML id of the hidden accessible input created by Prime Ng in the Dropdown component
   */
  public readonly countryCodeDropdownId = uniqueID();
  public readonly hasFocus = signal(false);

  /**
   * Fired when value of the native text input  has changed
   */
  public propagateChange: (val: string) => void;

  // Function to call when the input is touched.
  public onTouched: () => void;

  public setSelectedCountryCode(countryCode: number) {
    this.selectedCountryCode.set(countryCode);
  }

  /**
   * Reference to the form control created for this control value accessor component.
   * Makes it easy to access things like errors, status etc. for the control.
   */
  public controlRef: NgControl;
  public countryCodes: Array<SelectItem>;
  public countryCodeOverlayOptions: OverlayOptions;
  private language: string;

  readonly inputEl = viewChild<ElementRef<HTMLInputElement>>("input");
  readonly dropdown = viewChild<Dropdown>("dropdown");

  constructor(
    private cd: ChangeDetectorRef,
    private injector: Injector,
    private baseLocalizor: BiLocalizationHelperBaseService,
    public translator: BiTranslateService,
    public announcer: LiveAnnouncer
  ) {
    this.language = this.translator.currentLang;
  }

  public ngOnInit() {
    this.initCountryCodes();

    const phoneCode = this.phoneCode();
    if (phoneCode && this.canSelectCountryCode()) {
      this.countryId.set(this.baseLocalizor.getCountryIdByPhoneCode("+" + phoneCode));
    }

    this.setPhoneNumberValidation();

    this.selectedCountryCode.set(phoneCode ? phoneCode : this.baseLocalizor.getLanguageConfigByCountry(this.countryId()).phoneCountryCode);

    this.selectedPhoneType.set(this.phoneType() ? this.phoneType() : this.isLandLine() ? PhoneNumberTypeEnum.LandLine : PhoneNumberTypeEnum.Mobile);

    // Only set phone type if canSelectPhoneType is true and the phone type is set
    if (this.canSelectPhoneType() && this.phoneType()) {
      this.updatePhoneType();
    }

    if (this.hasLabel() && isStringNullOrEmpty(this.label())) {
      // set default label
      this.label.set(this.translator.instant("shared.CountryCodeAndMobile"));

      // We can't always assume that correct language is set, so listen to the language change event and set label accordingly
      this.translator.onLangChange.pipe(untilDestroyed(this), take(1)).subscribe(() => {
        this.label.set(this.translator.instant("shared.CountryCodeAndMobile"));
      });
    }
  }

  ngAfterViewInit() {
    // Using the Injector, we can inject the formcontrol bound to this component
    const control = this.injector.get(NgControl, undefined);
    if (control) this.controlRef = control;

    // Make sure that if user enters non-numeric chars, then remove them (input type "tel" or "number" allows for entering "-.,+'")
    this.controlRef.valueChanges
      .pipe(
        untilDestroyed(this),
        distinctUntilChanged(),
        filter(val => val != null),
        debounceTime(200)
      )
      .subscribe((val: string) => {
        // Determine if new value is a number or not.
        const newVal = val.replace(/[^0-9]/g, "");
        if (newVal !== val) {
          this.writeValue(newVal);
          this.propagateChange(newVal);
          this.cd.detectChanges();
        }
      });

    // As autofocus doesn't work on the native input, we set it here
    if (this.autofocus()) setTimeout(() => this.inputEl().nativeElement.focus(), 200);

    if (this.canSelectCountryCode()) {
      // As PrimeNg is niot setting the required "aria-controls" attribute for the hidden accessible input, we do it manually here
      document.getElementById(this.countryCodeDropdownId)?.setAttribute("aria-controls", this.dropdown().id + "_list");
    }

    this.initCountryCodeOverlayOptions();
  }

  writeValue(val: string): void {
    this.inputValue.set(val);
    if (!(this.cd as ViewRef).destroyed) this.cd.detectChanges();
  }

  onChange(val: string) {
    this.propagateChange(val);
    this.onTouched();
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  /**
   * Registers on touch event. This is to be called when the input loses focus (blur)
   */
  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
    // this.cd.detectChanges();
  }

  private setPhoneNumberValidation() {
    const languageConfig = this.baseLocalizor.getLanguageConfigByCountry(this.countryId());

    if (this.countryId() == BiCountryId.DK || this.countryId() == BiCountryId.NO) {
      this.phoneLength.set(languageConfig.minimumPhoneLength);
      this.phoneMaxLength.set(languageConfig.phoneLength);
      this.phoneLengthIsMinimum.set(false);
    } else if (this.countryId() == BiCountryId.SE || this.countryId() == BiCountryId.FI) {
      if (!this.isLandLine()) {
        this.phoneLength.set(languageConfig.minimumPhoneLength);
        this.phoneMaxLength.set(languageConfig.phoneLength);
        this.phoneLengthIsMinimum.set(true);
      } else {
        this.phoneLength.set(languageConfig.phoneLengthLandlineMin);
        this.phoneMaxLength.set(languageConfig.phoneLengthLandlineMax);
        this.phoneLengthIsMinimum.set(true);
      }
    } else if (this.countryId() == BiCountryId.DE) {
      this.phoneLength.set(languageConfig.minimumPhoneLength);
      this.phoneMaxLength.set(languageConfig.phoneLength);
      this.phoneLengthIsMinimum.set(true);
    } else if (this.countryId() == BiCountryId.EN) {
      if (!this.isLandLine()) {
        this.phoneLength.set(languageConfig.phoneLength);
        this.phoneLengthIsMinimum.set(false);
      } else {
        this.phoneLength.set(languageConfig.phoneLengthLandlineMin);
        this.phoneMaxLength.set(languageConfig.phoneLengthLandlineMax);
        this.phoneLengthIsMinimum.set(true);
      }
    }

    this._phonePrefix = "+" + languageConfig.phoneCountryCode;
  }

  public onCountryCodeChanged() {
    this.announcer.announce("+" + this.selectedCountryItem().value);

    this.phoneCode.set(this.selectedCountryItem().value);

    this.countryId.set(this.baseLocalizor.getCountryIdByPhoneCode("+" + this.selectedCountryItem().value));

    this.setPhoneNumberValidation();

    if (!isStringNullOrEmpty(this.inputValue())) {
      this.controlRef.control.markAsTouched();
      this.controlRef.control.markAsDirty();
      this.controlRef.control.updateValueAndValidity();
    }
  }

  public updatePhoneType() {
    this.phoneType.set(this.selectedPhoneType());

    this.isLandLine.set(this.selectedPhoneType() === PhoneNumberTypeEnum.LandLine);

    if (this.hasLabel()) {
      const mobileTranslation = this.translator.instant("shared.CountryCodeAndMobile");
      const landLineTranslation = this.translator.instant("shared.CountryCodeAndLandline");

      if (this.label() == mobileTranslation || this.label() == landLineTranslation || isStringNullOrEmpty(this.label())) {
        this.label.set(this.isLandLine() ? landLineTranslation : mobileTranslation);
      }
    }

    if (this.controlRef && this.controlRef.control) {
      this.controlRef.control.markAsTouched();
      this.controlRef.control.markAsDirty();
      this.controlRef.control.updateValueAndValidity();
    }

    this.setPhoneNumberValidation();
  }

  public validate(control: AbstractControl): ValidationErrors | null {
    if (isStringNullOrEmpty(control.value)) return null;

    return CustomValidators.phoneLengthValidator(this.countryId(), this.isLandLine())(control);
  }

  /**
   * Get the value of the input including the selected phone coutry code. This is ONLY relevant if canSelectCountryCode === true
   */
  public getValueWithCode(): string {
    const inputEl = this.inputEl();
    if (inputEl.nativeElement.value?.length > 0) {
      return "+" + this.selectedCountryItem().value + inputEl.nativeElement.value;
    }
    return undefined;
  }

  public getRawPhoneNumber(): number | undefined {
    const nativeInputVal = this.inputEl().nativeElement.value;
    if (nativeInputVal && nativeInputVal.length > 0) {
      const parsedValue = parseInt(nativeInputVal);
      if (!isNaN(parsedValue)) {
        return parsedValue;
      }
    }
    return undefined;
  }

  public getPhoneType() {
    return this.selectedPhoneType();
  }

  private initCountryCodes() {
    this.countryCodes = [];

    let countries: CountryCode[] = getCountries();
    const mainCountries: CountryCode[] = ["DK", "SE", "NO", "DE", "FI"];

    if (this.onlyMainCountries()) {
      countries = countries.filter(country => mainCountries.includes(country));
    }

    const otherCountryCodes = [];

    for (const country of countries) {
      const callingCode = +getCountryCallingCode(country);

      let countryName;
      switch (this.language) {
        case "da":
          countryName = this.countryCodeToDkCountryName[country];
          break;
        case "se":
          countryName = this.countryCodeToSeCountryName[country];
          break;
        case "en":
          countryName = this.countryCodeToEnCountryName[country];
          break;
        case "fi":
          countryName = this.countryCodeToFiCountryName[country];
          break;
        case "no":
          countryName = this.countryCodeToNoCountryName[country];
          break;
        case "de":
          countryName = this.countryCodeToDeCountryName[country];
          break;
      }

      let icon: string = country;
      if (icon.toString() === "AC") icon = "GB";
      if (icon.toString() === "TA") icon = "GB";

      if (callingCode) {
        const label = callingCode + " " + countryName;
        const value = callingCode;
        const title = countryName ?? country;

        if (mainCountries.includes(country)) {
          this.countryCodes.push({ value, label, icon, title });
        } else {
          otherCountryCodes.push({ value, label, icon, title });
        }
      }
    }

    this.countryCodes.sort((a, b) => {
      return a.value - b.value;
    });

    otherCountryCodes.sort((a, b) => {
      return a.title.localeCompare(b.title);
    });

    this.countryCodes = this.countryCodes.concat(otherCountryCodes);
  }

  /**
   * Method for parent components
   */
  public focusOnCountrycodeSelector() {
    document.getElementById(this.countryCodeDropdownId).focus();
  }

  private initCountryCodeOverlayOptions() {
    this.countryCodeOverlayOptions = {
      onShow: () => document.getElementById(this.dropdown().id + "_list")?.setAttribute("aria-label", this.translator.instant("accessibility.ListWithCountryCodes"))
    };
  }

  /**
   * TODO: remove this when PrimeNg has fixed its Dropdown focus trap issue!
   */
  public onCountryCodeKeyDown(event: KeyboardEvent) {
    if (event.key === "Tab") {
      if (!event.shiftKey) this.inputEl().nativeElement.focus();
      else document.getElementById(this.dropdown().inputId).blur();
    }
  }
}
